import { TFunction } from "react-i18next"
import { Certificate, CertificateKeys, ICertificateRowData } from "./CertificateSettings/types"
import CryptoProProvider from "./CryptoPro/CryptoProProvider"
import { Nullable } from "@app/types"
import { DateTime } from "luxon"

export const getCertificateKey = (certificate: Certificate): CertificateKeys => {
  if ('id' in certificate) return 'id'
  if ('validToDate' in certificate) return 'serialNumber'
  return 'oguid'
}

export const getCertificateData = (
  certificate: Certificate,
  crypto: CryptoProProvider,
  t: TFunction
): ICertificateRowData => {
  const getSubjectName = (subject: string): string => crypto.getSubjectInfo(subject, 'CN=').slice(3)

  const getSubjectType = (subject: string): string => {
    const employer = crypto.getSubjectInfo(subject, 'OGRNIP=')
    const organization = crypto.getSubjectInfo(subject, 'OGRN=')

    if (employer.length) {
      return t('crypto:sp')
    }

    if (organization.length) {
      return t('crypto:llc')
    }

    return ''
  }

  if ('id' in certificate) {
    const { CN, dateTo, dateFrom, id: key } = certificate

    return {
      dateFrom,
      dateTo,
      info: CN ? `${CN} (${t('crypto:cloud')})` : null,
      key
    }
  }

  if ('validToDate' in certificate) {
    const { validFromDate: dateFrom, validToDate: dateTo, serialNumber: key, subject } = certificate

    const name = getSubjectName(subject)
    const type = getSubjectType(subject)

    return {
      dateFrom,
      dateTo,
      info: `${type} ${name}`,
      key
    }
  }

  const { cn, dateFrom, dateTo, oguid } = certificate

  return {
    dateFrom,
    dateTo,
    info: cn ? `${cn} (${t('crypto:dsc')})` : null,
    key: oguid
  }
}

export const getCertificateValidity = (
  dateFrom: Nullable<string>,
  dateTo: Nullable<string>,
  crypto: CryptoProProvider,
  t: TFunction
): string => {
  if (!dateFrom && !dateTo) return t('envelopes:signAction.certificate.notSpecifiedAlt')

  const from = `${dateFrom ? `${t('envelopes:signAction.certificate.from')} ${crypto.getDate(dateFrom)}` : ''}`
  const to = `${dateTo ? ` ${t('envelopes:signAction.certificate.to')} ${crypto.getDate(dateTo)}` : ''}`

  return `${from}${to}`
}

export const getNextDay = (timestamp: number): number => (
  DateTime.fromMillis(timestamp).plus({days: 1}).toMillis()
)

export const getPrevDay = (timestamp: number): number => (
  DateTime.fromMillis(timestamp).minus({days: 1}).toMillis()
)

export const getDataFromLocalStorage = (key: string): Nullable<string> => localStorage.getItem(key)

const SINGULAR = 0
const PLURAL = 1

// ToDo: all languages support needs to be completed
export const getPluralCounter = (n: number): number => (n === 1 ? SINGULAR : PLURAL)
